import InfoBlock from './InfoBlock'
import DateInput from './inputs/Date'
import SsnInput from './inputs/SsnInput'
import CurrencyInput from './inputs/Currency'
import React, { ChangeEvent, FocusEvent, FC } from 'react'
import TelephoneInput from './inputs/Telephone'
import PercentInput from './inputs/PercentInput'
import PasswordInput from './inputs/PasswordInput'
import ZipCodeFetchInput from './inputs/ZipCodeFetchInput'
import { fullStoryBlockList } from '@mortgage-pos/ui/utils/fullStoryBlockList'
import { ClearButton } from './ClearButton'

export type Props = {
  name: string
  type: string
  value: string
  error: string
  title?: string
  touched: boolean
  subTitle: string
  maxLength: string
  placeholder?: string
  infoTitle?: string
  infoContent?: string
  modifierClass?: string
  infoWarning?: boolean
  setValue?: (value: any) => void
  onChange: (event: ChangeEvent<HTMLInputElement>) => {}
  onBlur?: (event: FocusEvent<HTMLInputElement>) => {}
}

export const Input: FC<Props> = ({
  name,
  title,
  modifierClass,
  subTitle,
  type,
  value,
  placeholder,
  infoTitle,
  infoContent,
  infoWarning,
  onChange,
  onBlur = (e) => {},
  setValue,
  maxLength,
  touched,
  error,
}) => {
  const spacerClassForDateInputs = type === 'date' ? '--top-spacing' : ''
  const shouldBlockFromFullstory = fullStoryBlockList.includes(name)

  const handleClear = () => {
    const syntheticEvent = {
      target: {
        name,
        value: '',
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>
    onChange(syntheticEvent)
  }
  const hasValue = value !== '' && value !== undefined && value !== null

  const clearButton = hasValue ? (
    <ClearButton name={name} onClick={handleClear} />
  ) : null
  return (
    <div
      className={`input ${shouldBlockFromFullstory ? 'fs-exclude' : ''} ${
        modifierClass ?? ''
      } ${error && touched ? '--error' : ''} ${spacerClassForDateInputs}`}
    >
      {title ? <h2 className="input__title">{title}</h2> : null}
      {subTitle && type !== 'date' ? (
        <label className="input__subtitle" htmlFor={name}>
          {subTitle}
        </label>
      ) : null}
      {(() => {
        switch (type) {
          case 'tel':
            return (
              <div className="input__telephone-wrapper">
                <TelephoneInput
                  aria-label={name}
                  value={value}
                  name={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder={placeholder}
                  data-testid={name}
                />
                {clearButton}
              </div>
            )
          case 'currency':
            return (
              <div className="input__currency-wrapper">
                <CurrencyInput
                  aria-label={name}
                  value={value}
                  name={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder={placeholder}
                  data-testid={name}
                />
                {clearButton}
              </div>
            )
          case 'ssn':
            return (
              <SsnInput
                aria-label={name}
                value={value}
                name={name}
                onChange={onChange}
                onBlur={onBlur}
                data-testid={name}
              />
            )
          case 'password':
            return (
              <div className="input__password-wrapper">
                <PasswordInput
                  value={value}
                  name={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  data-testid={name}
                />
                {clearButton}
              </div>
            )
          case 'percent':
            return (
              <PercentInput
                value={value}
                name={name}
                onChange={onChange}
                onBlur={onBlur}
                data-testid={name}
              />
            )
          case 'date':
            return (
              <DateInput
                subTitle={subTitle}
                name={name}
                value={value}
                setValue={setValue}
              />
            )
          case 'zip':
            return (
              <div className="input__zipcode-wrapper">
                <ZipCodeFetchInput
                  aria-label={name}
                  value={value}
                  name={name}
                  onChange={onChange}
                  placeholder={placeholder}
                  data-testid={name}
                />
                {clearButton}
              </div>
            )
          default:
            return (
              <div className="input__field-wrapper">
                <input
                  className="input__field"
                  type={type}
                  name={name}
                  value={value}
                  maxLength={maxLength ? Number(maxLength) : undefined}
                  aria-label={name}
                  placeholder={placeholder}
                  onChange={onChange}
                  onBlur={onBlur}
                  data-testid={name}
                />
                {clearButton}
              </div>
            )
        }
      })()}
      {error && touched ? <InfoBlock type="danger">{error}</InfoBlock> : null}
      {infoTitle || infoContent ? (
        <InfoBlock type={infoWarning ? 'danger' : 'info'} title={infoTitle}>
          {infoContent}
        </InfoBlock>
      ) : null}
    </div>
  )
}

export default Input
